import React, { useEffect } from 'react'
import { useMeasure, useLockBodyScroll, useDebounce } from 'react-use'
import {
  Flex,
  Row,
  Col,
  Radio,
  Input,
  Table,
  Badge,
  Spin,
  Empty,
  Button,
  Tooltip,
  Select,
  Splitter,
  Divider,
} from 'antd'
import { useParams, useLocation } from 'react-router'
import { useGlobalSearch } from '../hooks/GlobalSearch'
import { requireAuthentication } from '../helpers/authentication'
import MustHave from '../containers/MustHave'

import { useHistory } from 'react-router-dom'
import { setGoBackToVehicleTest } from '../actions/go-to-vehicle-test'
import {
  useGlobalSearchContext,
  useGlobalSearchDispatch,
} from './GlobalSearchContext'
import { FormattedMessage, injectIntl } from 'react-intl'
import { LocationImage } from '../FunctionalDiagrams/Image'
import CircuitColorIcon from '../FunctionalDiagrams/CircuitColorIcon'
import PinoutContainer from '../containers/PinoutContainer'
import { useComponentDetails } from '../hooks/ComponentDetails'
import Loading from '../components/Loading'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { setVIN, resetVIN } from '../actions/app-actions'
import {
  DtcResults,
  ManualDtcs,
  GenerateEphemeralVehicleTestButton,
} from './ManualDTC'
import { FUNCTIONAL_DIAGRAMS_FLAG } from '../constants'
import { RelatedSubsystemsButton } from '../components/RelatedSubsystemsButton'
import RelatedContentFor from '../components/RelatedContentButton'
import { SubsystemsViewTable } from '../components/SubsystemTable'
import { useSearchSubsystems } from '../BlockDiagram/hooks'
import { needsPinsForRelatedDiagrams } from '../FunctionalDiagrams/RelatedDiagramsModal'
import { ConnectorPreview } from '../components/ConnectorPreview/index'
import { useCheckPermission } from '../hooks/Permissions'
import { useFeatureFlags } from '../hooks/FeatureFlags'
import { useMakeModelByVin } from '../hooks/MakeModels'
import { useRelatedDTCs } from '../hooks/CircuitDTCs'
import { useStations } from '../hooks/Stations'
import { usePlants } from '../hooks/Plants'
import StationTab from './StationTab'

import {
  RelatedDTCsModal,
  CircuitDTCsProvider,
  RelatedDTCsIconButton,
} from '../CircuitDTCs'
import './style.scss'
import { DARK_MODE } from '../constants'
import { useQueryClient } from 'react-query'
import uuidv4 from 'uuid/v4'
import {
  dispatchEvent,
  addPageAttribute,
  removePageAttribute,
} from '../CloudWatch-init'
import { useUserInfo, useUserPlant } from '../hooks/User'

const SEARCH_RESULT_CATEGORY = {
  component: 'component',
  device: 'device',
  circuit: 'circuit',
  dtc: 'dtc',
  'functional-diagrams': 'functional-diagrams',
}

const Counter = ({ isLoading, results }) => {
  const theme = useSelector((state) => state.app.theme, shallowEqual)
  return isLoading ? (
    <Spin />
  ) : (
    <Badge
      color={theme == DARK_MODE ? 'white' : '#0189e9'}
      count={results ? results.length : 0}
      showZero
    />
  )
}

const Pinout = ({ vin, componentId }) => {
  const { componentDetails, isLoading } = useComponentDetails({
    vin,
    componentId,
  })
  return isLoading ? (
    <Loading useBackgroundColor={false} />
  ) : (
    <PinoutContainer
      component={componentDetails.component}
      vin={vin}
      showEmptyPinsToggle={false}
      showEmptyPinsDefault={true}
      enablePinSelection={true}
    />
  )
}

const NoData = ({ description }) => {
  return (
    <div className="empty">
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<div className="empty-description">{description}</div>}
      />
    </div>
  )
}

// Which entity the Related Content button asks about follows the results tab the
// user is looking at, since that is what the selection means.
//
// A circuit row is a component that carries a circuit, and selecting one sets
// the same componentId a component row does -- which is why the preview pane
// and the Related Diagrams button beside this one already treat it as a
// component. Hiding this button alone would read as a bug rather than a
// decision, so circuits ask the connector question too.
//
// Functional diagrams have no entity of their own. DTCs do, but they are
// reached from the DTC list instead: selection here is multi-row and feeds the
// ephemeral vehicle test, which is a different question from "tell me about
// this one fault".
const relatedContentForSelection = (searchResultCategory, selection) => {
  if (!selection.componentId) {
    return null
  }

  switch (searchResultCategory) {
    case SEARCH_RESULT_CATEGORY.component:
    case SEARCH_RESULT_CATEGORY.circuit:
      // Two kinds, one button: what the connector is used for, and how to
      // service it. Concept-T publishes both against the component id.
      return {
        label: 'generic.connector',
        targets: [
          { entityKind: 'connector', connector_id: selection.componentId },
          { entityKind: 'connector_part', connector_id: selection.componentId },
        ],
      }
    case SEARCH_RESULT_CATEGORY.device:
      return {
        label: 'generic.device',
        targets: [{ entityKind: 'device', device_id: selection.componentId }],
      }
    default:
      return null
  }
}

const LocationOrPinout = ({
  intl,
  clickHandler,
  onSVGLoad,
  selectedElementId,
  loactionOrPinout,
  setLocationOrPinout,
  makeModel,
  searchResultCategory,
}) => {
  const LOCATION = 'location'
  const PINOUT = 'pinout'
  const STATION = 'station'
  const globalSearchContext = useGlobalSearchContext()
  const globalSearchDispatch = useGlobalSearchDispatch()
  const vin = globalSearchContext.vin
    ? globalSearchContext.vin
    : window.location.pathname.split('/')[3]
  // Resolved when the selection changes and deliberately not when the results
  // tab does. The tab tells us what kind of thing was picked, but only at the
  // moment of picking -- afterwards the selection stands on its own, and
  // switching tabs to look at other results should not re-ask about it. Keying
  // on the tab as well changes the query keys underneath an unchanged selection,
  // which refetches, flashes a spinner, and briefly shows a count for the wrong
  // kind.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const relatedContentTarget = React.useMemo(
    () =>
      relatedContentForSelection(
        searchResultCategory,
        globalSearchContext.selection,
      ),
    [globalSearchContext.selection.componentId],
  )
  const { isLoading: plantsIsLoading, plants: modelPlants } = usePlants({
    make_model_id: makeModel ? makeModel.id : null,
    vin,
  })
  const [selectedPlant, setSelectedPlant] = React.useState(null)
  const { isLoading: stationsIsLoading, stations } = useStations({
    plant_name: selectedPlant
      ? selectedPlant
      : modelPlants?.[0]
        ? modelPlants[0].name
        : null,
    make_model_id: makeModel ? makeModel.id : null,
  })
  const { alias, color, imageName, partNumber } = globalSearchContext.selection
  const checkUserPermission = useCheckPermission()
  const { isLoading: userPermissionLoading, usersFeatureFlags } =
    useFeatureFlags()
  const theme = useSelector((state) => state.app.theme, shallowEqual)

  const checkPermission = () => {
    if (!userPermissionLoading) {
      const featurePermission = usersFeatureFlags.includes(
        FUNCTIONAL_DIAGRAMS_FLAG,
      )
      const userPermission = checkUserPermission('page-functional-diagrams:get')
      return featurePermission && userPermission
    } else {
      return false
    }
  }

  useEffect(() => {
    globalSearchDispatch.handleRelatedSubsystemComponentId({
      relatedSubsystemComponentID: null,
    })
  }, [globalSearchContext.selection.componentId])

  const handlePlantChange = (value) => {
    setSelectedPlant(value)
  }

  const plantLabel = intl.formatMessage({
    id: 'globalSearch.stations.plantLabel',
  })
  const showSelectedView = () => {
    switch (loactionOrPinout) {
      case LOCATION:
        return globalSearchContext.selection.locationImageName ? (
          <>
            <LocationImage
              fileName={globalSearchContext.selection.locationImageName}
              componentId={globalSearchContext.selection.componentId}
              clickHandler={clickHandler}
              onSVGLoad={onSVGLoad}
              selectedElementId={selectedElementId}
              displayHotspots={true}
            />
            <div className="global-search-component-overlay">
              <ConnectorPreview
                title={alias}
                colorCode={color}
                fileName={imageName}
                theme={theme}
                partNumber={partNumber}
              />
            </div>
          </>
        ) : (
          <NoData description="No Image" />
        )
      case PINOUT:
        return (
          <Pinout
            vin={globalSearchContext.vin}
            componentId={globalSearchContext.selection.componentId}
          />
        )
      case STATION:
        return selectedPlant || modelPlants ? (
          <StationTab
            plant_name={
              selectedPlant
                ? selectedPlant
                : modelPlants?.[0]
                  ? modelPlants[0].name
                  : null
            }
            plantDisplayName={plantsIsLoading ? '...' : modelPlants[0].name}
            plantLabel={plantLabel}
            makeModel={makeModel}
            stations={stations}
            isLoading={stationsIsLoading}
            intl={intl}
          />
        ) : (
          <NoData>
            {intl.formatMessage({
              id: 'globalSearch.stations.noPlantSelected',
            })}
          </NoData>
        )
      default:
        return ''
    }
  }

  if (globalSearchContext.selection.componentId === null) {
    return <NoData description="Select Component" />
  }

  return (
    <Flex vertical gap="middle" className="component-location-or-pinout">
      <Flex
        vertical={false}
        justify="space-between"
        className="component-location-or-pinout-toggle"
      >
        <Radio.Group
          value={loactionOrPinout}
          buttonStyle="solid"
          size="middle"
          onChange={(event) => setLocationOrPinout(event.target.value)}
        >
          <Radio.Button value={LOCATION}>
            <FormattedMessage id="globalSearch.previewSwitch.componentLocation" />
          </Radio.Button>
          <Radio.Button value={PINOUT}>
            <FormattedMessage id="globalSearch.previewSwitch.pinout" />
          </Radio.Button>
          <MustHave permission="station-mapping:edit">
            <Radio.Button
              value={STATION}
              disabled={globalSearchContext.selection.componentAlias === ''}
            >
              <FormattedMessage id="globalSearch.previewSwitch.station" />
            </Radio.Button>
          </MustHave>
        </Radio.Group>
        {/* The pinout view carries its own Related Diagrams button inside the
            table header, which does this and more — it is pin-aware. Showing both
            would be two buttons for one job. */}
        {checkPermission() &&
          loactionOrPinout !== PINOUT &&
          // This button only ever asks the no-pins question, so the categories
          // that need a pin selection have nothing to show here.
          !needsPinsForRelatedDiagrams(
            globalSearchContext.selection.category,
          ) && (
            <>
              <div className="global-search-related-subsystem-button">
                <RelatedSubsystemsButton
                  showButtonText={true}
                  vin={globalSearchContext.vin}
                  componentId={globalSearchContext.selection.componentId}
                  contextComponentId={
                    globalSearchContext.subsystems.componentId
                  }
                  componentIdDispatch={
                    globalSearchDispatch.handleRelatedSubsystemComponentId
                  }
                />
              </div>
            </>
          )}
        {relatedContentTarget && (
          <div className="global-search-related-content-button">
            <RelatedContentFor
              targets={relatedContentTarget.targets}
              vin={vin}
              details={{
                label: relatedContentTarget.label,
                pin: globalSearchContext.selection.alias,
                description: globalSearchContext.selection.componentId,
              }}
            />
          </div>
        )}
      </Flex>
      <div className="component-location-or-pinout-view">
        {showSelectedView()}
      </div>
    </Flex>
  )
}

const ResultsTable = ({
  columns,
  results,
  isLoading,
  dataTestId,
  rowKey,
  setSelectedRowKeys,
  selectedRowKeys,
  expandable = false, // https://4x.ant.design/components/table/#expandable
  onChange,
  pageSize,
  selectFirstItemInitially = false,
}) => {
  const [containerRef, { height: containerHeight }] = useMeasure()
  const globalSearchDispatch = useGlobalSearchDispatch()
  const [tableHeight, setTableHeight] = React.useState(0)

  // Auto-select the first item if requested
  React.useEffect(() => {
    if (
      selectFirstItemInitially &&
      results &&
      results.length > 0 &&
      selectedRowKeys &&
      selectedRowKeys.length === 0
    ) {
      setSelectedRowKeys([rowKey(results[0])])
      globalSearchDispatch.handleComponentSelection({
        componentId: results[0].componentId,
        componentAlias: results[0].alias,
        componentStation: results[0].assembly_station,
        componentSalesCode: results[0].sales_code,
        locationImageName: results[0].locationImageName,
        color: results[0].color,
        alias: results[0].alias,
        imageName: results[0].imageName,
        category: results[0].category,
        partNumber: results[0].partNumber,
      })
    }
  }, [
    selectFirstItemInitially,
    results,
    selectedRowKeys,
    setSelectedRowKeys,
    rowKey,
    globalSearchDispatch,
  ])

  // containerHeight changes its height multiple times during re-render
  // but only the largest (last) value is needed.
  useDebounce(
    () => {
      setTableHeight(containerHeight - 50) // 50 is the height of header.
    },
    1,
    [containerHeight],
  )

  const onSelectDefaultCallback = (
    record,
    selected,
    selectedRows,
    nativeEvent,
  ) => {
    setSelectedRowKeys([rowKey(record)])
    globalSearchDispatch.handleComponentSelection({
      componentId: record.componentId,
      componentAlias: record.alias,
      componentStation: record.assembly_station,
      componentSalesCode: record.sales_code,
      locationImageName: record.locationImageName,
      color: record.color,
      alias: record.alias,
      imageName: record.imageName,
      category: record.category,
      partNumber: record.partNumber,
    })
  }

  return (
    <div className="global-search-results-table" ref={containerRef}>
      <Table
        data-testid={dataTestId}
        size="middle"
        columns={columns}
        dataSource={results}
        loading={isLoading}
        pagination={
          pageSize
            ? {
                pageSize: pageSize,
                hideOnSinglePage: true,
                showSizeChanger: false,
              }
            : false
        }
        scroll={{
          y: tableHeight,
        }}
        rowKey={rowKey}
        rowSelection={{
          type: 'radio',
          onSelect: onSelectDefaultCallback,
          selectedRowKeys: selectedRowKeys,
        }}
        expandable={expandable}
        onChange={(pagination, filters, sorter, extra) => {
          onChange({ filters })
        }}
      />
    </div>
  )
}

const commonColumns = [
  {
    title: <FormattedMessage id="globalSearch.resultsTable.commonColumns.id" />,
    dataIndex: 'componentId',
    key: 'componentId',
    ellipsis: true,
    width: '20%',
    render: (componentId) => (
      <Tooltip placement="topLeft" title={componentId}>
        {componentId}
      </Tooltip>
    ),
  },
  {
    title: (
      <FormattedMessage id="globalSearch.resultsTable.commonColumns.alias" />
    ),
    dataIndex: 'alias',
    key: 'alias',
    ellipsis: true,
    render: (alias) => (
      <Tooltip placement="topLeft" title={alias}>
        {alias}
      </Tooltip>
    ),
  },
  {
    title: (
      <FormattedMessage id="globalSearch.resultsTable.commonColumns.description" />
    ),
    dataIndex: ['description'],
    key: 'description',
    ellipsis: true,
    width: '25%',
    render: (description, obj) => {
      let msg = description
      if (
        obj.hasOwnProperty('translated_description') &&
        obj.translated_description
      ) {
        msg = obj.translated_description
      }
      return (
        <Tooltip placement="topLeft" title={msg}>
          {msg}
        </Tooltip>
      )
    },
  },
]

const DeviceResults = ({
  results,
  isLoading,
  setSelectedRowKeys,
  selectedRowKeys,
}) => {
  const columns = [...commonColumns]
  const user = useSelector((state) => state.user.data, shallowEqual)

  if (!user.organization_type || user.organization_type !== 'dealer') {
    columns.push({
      title: (
        <FormattedMessage id="globalSearch.resultsTable.commonColumns.station" />
      ),
      dataIndex: ['assembly_station'],
      key: 'station',
    })
  }
  columns[1].filters = undefined
  columns[1].onFilter = undefined

  return (
    <ResultsTable
      columns={columns}
      results={results}
      loading={isLoading}
      dataTestId="device-results"
      rowKey={(record) => record.componentId}
      setSelectedRowKeys={setSelectedRowKeys}
      selectedRowKeys={selectedRowKeys}
    />
  )
}

const FunctionalDiagramsResults = ({
  results,
  isLoading,
  setSelectedRowKeys,
  selectedRowKeys,
}) => {
  const history = useHistory()
  const globalSearchContext = useGlobalSearchContext()
  const selectSubsystem = ({ record }) => {
    history.push({
      pathname: `/functional-diagrams/vin/${globalSearchContext.vin}`,
      search: `?subsystem=${record.id}`,
    })
  }

  return (
    <SubsystemsViewTable
      subsystems={results}
      pagination={false}
      susbsystemCallback={selectSubsystem}
    />
  )
}

export const CircuitResults = ({
  results,
  isLoading,
  setSelectedRowKeys,
  selectedRowKeys,
  activeFilters,
  setActiveFilters,
  makeModel,
}) => {
  const user = useSelector((state) => state.user.data, shallowEqual)
  const [filters, setFilters] = React.useState([])
  const [circuits, setCircuits] = React.useState([])
  const [relatedDtcs, setRelatedDtcs] = React.useState({})

  const { isLoading: circuitDTCsLoading, dtcs } = useRelatedDTCs({
    makeModelId: makeModel?.id,
    circuits: [...circuits].sort((a, b) => a.localeCompare(b)),
  })

  React.useEffect(() => {
    if (!results) return
    setCircuits([
      ...new Set(results.map((component) => component.circuit.circuit)),
    ])
  }, [results])

  React.useEffect(() => {
    if (!circuits) return
    setFilters(circuits.map((circuit) => ({ text: circuit, value: circuit })))
  }, [circuits])

  React.useEffect(() => {
    if (!dtcs) return
    setRelatedDtcs(
      dtcs.reduce((acc, obj) => {
        const circuit = obj['circuit']
        if (!acc[circuit]) {
          acc[circuit] = []
        }
        acc[circuit].push(obj)
        return acc
      }, {}),
    )
  }, [dtcs])

  const columns = [
    ...commonColumns,
    Table.EXPAND_COLUMN,
    {
      title: (
        <FormattedMessage id="globalSearch.resultsTable.circuitResults.circuit" />
      ),
      dataIndex: ['circuit', 'circuit'],
      key: 'circuit',
      filters,
      onFilter: (value, record) => record.circuit.circuit === value,
      ellipsis: true,
      filteredValue: activeFilters,
    },
  ]

  if (!user.organization_type || user.organization_type !== 'dealer') {
    columns.push({
      title: (
        <FormattedMessage id="globalSearch.resultsTable.commonColumns.station" />
      ),
      dataIndex: ['assembly_station'],
      key: 'station',
      ellipsis: true,
    })
  }

  columns[1].filters = undefined
  columns[1].onFilter = undefined

  columns.push({
    title: (
      <FormattedMessage id="globalSearch.resultsTable.circuitResults.dtcs" />
    ),
    render: (value, record, index) => (
      <CircuitDTCsProvider>
        <RelatedDTCsIconButton
          relatedDtcs={relatedDtcs[record.circuit.circuit]}
          disabled={!(record.circuit.circuit in relatedDtcs)}
          makeModelId={makeModel?.id}
          circuit={record.circuit.circuit}
          loading={circuitDTCsLoading}
        />
        <RelatedDTCsModal />
      </CircuitDTCsProvider>
    ),
    width: '7%',
  })

  const onChangeCallback = ({ filters }) => {
    setActiveFilters(filters.circuit)
  }

  return (
    <ResultsTable
      columns={columns}
      results={results}
      loading={isLoading}
      dataTestId="circuit-results"
      rowKey={(record) => record.id}
      setSelectedRowKeys={setSelectedRowKeys}
      selectedRowKeys={selectedRowKeys}
      expandable={{
        expandedRowRender: (record, index, indent, expanded) => {
          const [color1, color2] = record.circuit.colors.hex_triplets
          // The authoring team's terminology overrides the imported description
          // when present. `circuit_translated_description` and
          // `harness_translated_description` are each looked up independently (by
          // the circuit's own code, and the harness's own code) - unlike
          // `translated_description`, which also falls back to the harness's term
          // for search-text matching and would otherwise make this field show the
          // harness's name even when the circuit itself has no term of its own. See
          // `core.search.add_terminology_to_graph`.
          const circuitDescription =
            record.circuit.circuit_translated_description ||
            record.circuit.description
          const harnessDescription =
            record.circuit.harness_translated_description ||
            record.circuit.harness_description
          return (
            <div className="circuit-details-expand">
              <div>
                <FormattedMessage id="globalSearch.resultsTable.circuitResults.details.description" />
                : {circuitDescription}
              </div>
              <div>
                <FormattedMessage id="globalSearch.resultsTable.circuitResults.details.color" />
                :{' '}
                {
                  <CircuitColorIcon
                    firstColor={color1}
                    secondColor={color2}
                    className="inline-color-icon"
                  />
                }{' '}
                {record.circuit.colors.letter_code}
              </div>
              <div>
                <FormattedMessage id="globalSearch.resultsTable.circuitResults.details.pin" />
                : {record.pin}
              </div>
              <div>
                <FormattedMessage id="globalSearch.resultsTable.circuitResults.details.harnessDescription" />
                : {harnessDescription}
              </div>
            </div>
          )
        },
        rowExpandable: (record) => true,
        showExpandColumn: true,
      }}
      onChange={onChangeCallback}
    />
  )
}

const ComponentResults = ({
  results,
  isLoading,
  setSelectedRowKeys,
  selectedRowKeys,
  activeFilters,
  setActiveFilters,
}) => {
  const user = useSelector((state) => state.user.data, shallowEqual)
  const [filters, setFilters] = React.useState([])

  React.useEffect(() => {
    if (!results) return
    setFilters(
      [...new Set(results.map((component) => component.alias))].map(
        (alias) => ({ text: alias, value: alias }),
      ),
    )
  }, [results])

  const columns = [...commonColumns, Table.EXPAND_COLUMN]

  if (!user.organization_type || user.organization_type !== 'dealer') {
    columns.push({
      title: (
        <FormattedMessage id="globalSearch.resultsTable.commonColumns.station" />
      ),
      dataIndex: ['assembly_station'],
      key: 'station',
    })
  }

  columns[1].filters = filters
  columns[1].onFilter = (value, record) => record.alias === value
  columns[1].filteredValue = activeFilters

  const onChangeCallback = ({ filters }) => {
    setActiveFilters(filters.alias)
  }

  return (
    <ResultsTable
      columns={columns}
      results={results}
      loading={isLoading}
      dataTestId="component-results"
      rowKey={(record) => record.componentId}
      setSelectedRowKeys={setSelectedRowKeys}
      selectedRowKeys={selectedRowKeys}
      selectFirstItemInitially={true}
      expandable={{
        expandedRowRender: (record, index, indent, expanded) => {
          return (
            <div className="circuit-details-expand">
              <FormattedMessage id="globalSearch.resultsTable.componentResults.details.device" />
              : {record.deviceId}
            </div>
          )
        },
        rowExpandable: (record) => true,
        showExpandColumn: true,
      }}
      onChange={onChangeCallback}
    />
  )
}

const SVGSelectionTable = ({ componentList, onRowSelect }) => {
  const columns = [...commonColumns]
  const user = useSelector((state) => state.user.data, shallowEqual)

  if (!user.organization_type || user.organization_type !== 'dealer') {
    columns.push({
      title: (
        <FormattedMessage id="globalSearch.resultsTable.commonColumns.station" />
      ),
      dataIndex: ['assembly_station'],
      key: 'station',
    })
  }
  columns[1].filters = undefined
  columns[1].onFilter = undefined

  return (
    <div className="global-search-svg-component-list">
      <Table
        data-testid="svg-components-list"
        size="small"
        columns={columns}
        dataSource={componentList}
        pagination={{
          pageSize: 2,
          hideOnSinglePage: true,
          showSizeChanger: false,
        }}
        rowKey={(record) => record.componentId}
        rowSelection={{
          type: 'radio',
          onSelect: onRowSelect,
          selectedRowKeys: [],
        }}
      />
    </div>
  )
}

function GlobalSearchPage({ intl }) {
  // Hydrate goBackToVehicleTest from sessionStorage or URL cache_id if not already set
  const goBackToVehicleTest = useSelector(
    (state) => state.goBackToVehicleTest,
    shallowEqual,
  )
  const location = useLocation()
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const cacheId = params.get('cache_id')
    // Only hydrate if not already set
    if (!goBackToVehicleTest || !goBackToVehicleTest.pathname) {
      // Try sessionStorage first
      const stored = window.sessionStorage.getItem('goBackToVehicleTest')
      if (stored) {
        try {
          const parsed = JSON.parse(stored)
          if (parsed && parsed.pathname) {
            dispatch({ type: 'SET_GO_BACK_TO_VEHICLE_TEST', ...parsed })
            return
          }
        } catch (e) {}
      }
      // If no sessionStorage, but cache_id and vin are present, set directly
      if (cacheId && vin) {
        const vehicleTestUrl = `/tracer/${cacheId}`
        dispatch({
          type: 'SET_GO_BACK_TO_VEHICLE_TEST',
          pathname: vehicleTestUrl,
          vin,
          cache_id: cacheId,
        })
      }
    }
  }, [location.search, vin])
  const LOCATION = 'location'
  const STATION = 'station'
  const dispatch = useDispatch()
  const history = useHistory()
  const globalSearchContext = useGlobalSearchContext()
  const globalSearchDispatch = useGlobalSearchDispatch()
  const { makeModel } = useMakeModelByVin({
    vin: globalSearchContext.vin,
  })
  const { vin } = useParams()
  const { userInfo } = useUserInfo()
  const [userPlant] = useUserPlant()
  const userName = userInfo?.username || ''
  const userId = userInfo?.client_id || ''

  // Stable session ID for correlating all events from this page visit
  const sessionId = React.useRef(uuidv4())

  // Track time spent on the page
  const timeOnGlobalSearchPage = React.useRef(null)

  const sendEventUpdateTimeOnPage = () => {
    if (timeOnGlobalSearchPage.current) {
      dispatchEvent &&
        userPlant &&
        userName &&
        userId &&
        dispatchEvent('GLOBAL_SEARCH_PAGE_TIME_SPENT', {
          vin,
          plant: userPlant,
          username: userName,
          user_id: userId,
          timeOnPage: Date.now() - timeOnGlobalSearchPage.current,
          session_id: sessionId.current,
        })
    }
  }

  // Log page load event and set page attributes
  useEffect(() => {
    timeOnGlobalSearchPage.current = Date.now()
    dispatchEvent &&
      userPlant &&
      userName &&
      userId &&
      dispatchEvent('GLOBAL_SEARCH_PAGE_LOADED', {
        vin,
        plant: userPlant,
        username: userName,
        user_id: userId,
        session_id: sessionId.current,
      })
    addPageAttribute({
      globalSearchUser: userName,
      globalSearchUserId: userId,
      globalSearchPlant: userPlant,
      globalSearchVin: vin,
    })
    return () => {
      sendEventUpdateTimeOnPage()
      removePageAttribute('globalSearchUser')
      removePageAttribute('globalSearchUserId')
      removePageAttribute('globalSearchPlant')
      removePageAttribute('globalSearchVin')
    }
  }, [userName, userId, userPlant, vin])

  // Send time-on-page event every 15 minutes
  useEffect(() => {
    const eventIntervalId = setInterval(() => {
      sendEventUpdateTimeOnPage()
    }, 900000)
    return () => {
      clearInterval(eventIntervalId)
    }
  }, [])

  const [componentListSearchString, updateComponentListSearchString] =
    React.useState(null)

  const { results, isLoading } = useGlobalSearch({
    vin: globalSearchContext.vin,
    searchString: globalSearchContext.searchString,
  })

  const { results: componentList, isLoading: componentListLoading } =
    useGlobalSearch({
      vin: vin,
      searchString: componentListSearchString,
    })

  const { searchSubsystems } = useSearchSubsystems({
    vin: globalSearchContext.vin,
    componentId: globalSearchContext.subsystems.componentId,
  })

  const [circuitActiveFilters, setCircuitActiveFilters] = React.useState([])
  const [componentActiveFilters, setComponentActiveFilters] = React.useState([])
  const [resultsByCircuits, setResultsByCircuits] = React.useState([])
  const [resultsByDevice, setResultsByDevice] = React.useState([])
  const [resultsByComponent, setResultsByComponent] = React.useState([])
  const [resultsByDtc, setResultsByDtc] = React.useState([])
  const [resultsByFunctionalDiagrams, setResultsByFunctionalDiagrams] =
    React.useState([])
  const [resultState, updateResultState] = React.useState(null)
  const [searchResultCategory, setSearchResultCategory] = React.useState(
    SEARCH_RESULT_CATEGORY.component,
  )
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([])
  const [searchStr, updateSearchStr] = React.useState(null)
  const [componentListState, updateComponentListState] = React.useState(null)
  const [selectedElementId, updateSelectedElementId] = React.useState(null)
  const [selectionSet, updateSelectionSet] = React.useState(new Set())
  const [loactionOrPinout, setLocationOrPinout] = React.useState(LOCATION)

  useLockBodyScroll(true)

  React.useEffect(() => {
    if (!results) {
      updateResultState([])
    } else {
      updateResultState(results)
    }
  }, [results])

  React.useEffect(() => {
    removeSelectionHandler()
  }, [selectedRowKeys])

  React.useEffect(() => {
    if (globalSearchContext.subsystems.componentId) {
      removeSelectionHandler()
      if (selectedElementId) {
        const selectedElement = document.getElementById(selectedElementId)
        if (selectedElement) selectedElement.setAttribute('fill-opacity', '0')
        updateSelectedElementId(null)
      }
    }
  }, [globalSearchContext.subsystems.componentId])

  React.useEffect(() => {
    dispatch(setVIN(vin))
    return () => dispatch(resetVIN())
  }, [vin, dispatch])

  React.useEffect(() => {
    if (!resultState) {
      globalSearchDispatch.handleRelatedSubsystemComponentId({
        relatedSubsystemComponentID: null,
      })
    }
  }, [resultState])

  React.useEffect(() => {
    if (!resultState) {
      setResultsByCircuits([])
    } else {
      setResultsByCircuits(
        resultState
          .filter((result) => result.search_category === 'circuit')
          .map((result) => ({ ...result, id: crypto.randomUUID() })),
      )
    }
  }, [resultState])

  React.useEffect(() => {
    if (!resultState) {
      setResultsByDevice([])
    } else {
      setResultsByDevice(
        resultState.filter((result) => result.search_category === 'device'),
      )
    }
  }, [resultState])

  React.useEffect(() => {
    if (!resultState) {
      setResultsByComponent([])
    } else {
      setResultsByComponent(
        resultState.filter((result) => result.search_category === 'component'),
      )
    }
  }, [resultState])

  React.useEffect(() => {
    if (!resultState) {
      setResultsByDtc([])
    } else {
      setResultsByDtc(
        resultState.filter((result) => result.search_category === 'dtc'),
      )
    }
  }, [resultState])

  React.useEffect(() => {
    if (!resultState) {
      setResultsByFunctionalDiagrams([])
    } else {
      setResultsByFunctionalDiagrams(
        resultState.filter(
          (result) => result.search_category === 'functional-diagrams',
        ),
      )
    }
  }, [resultState])

  React.useEffect(() => {
    if (componentList && selectionSet.size !== 0) {
      filterComponents(selectionSet)
      updateSelectionSet(new Set())
    }
  }, [componentList])

  // Get search_string from query params and autofill input, then actually submit the search using the same logic as the form
  React.useEffect(() => {
    const params = new URLSearchParams(location.search)
    const searchString = params.get('search_string')
    const cacheId = params.get('cache_id')
    if (cacheId) {
      // Build canonical vehicle test URL for back button usage
      const vehicleTestUrl = `/tracer/${cacheId}`
      setGoBackToVehicleTest({
        pathname: vehicleTestUrl,
        vin,
        cache_id: cacheId,
      })
      // Check sessionStorage after setting
      setTimeout(() => {}, 500)
    }
    if (searchString) {
      resetState()
      updateComponentListState(null)
      updateSearchStr(searchString)
      // Directly update context and trigger search with the correct value
      globalSearchDispatch.handleSearch({
        vin,
        searchString,
      })
    }
  }, [location, vin])

  const onSVGElementClick = (elements, targetId) => {
    if (elements && targetId) {
      const componentSet = new Set(
        elements.map((element) => element.id.substring(4)),
      )
      if (componentList) {
        filterComponents(componentSet)
      } else {
        updateSelectionSet(componentSet)
      }
      updateSelectedElementId(targetId)
    } else {
      removeSelectionHandler()
    }
  }

  const filterComponents = (elements) => {
    const list = componentList.filter((component) =>
      elements.has(component.componentId),
    )
    globalSearchDispatch.handleRelatedSubsystemComponentId({
      relatedSubsystemComponentID: null,
    })
    updateComponentListState(list)
  }

  const onSVGTableRowSelect = (record, selected, selectedRows, nativeEvent) => {
    resetState()
    globalSearchDispatch.handleRelatedSubsystemComponentId({
      relatedSubsystemComponentID: null,
    })
    updateSearchStr(record.componentId)
    globalSearchDispatch.handleSearch({
      vin,
      searchString: record.componentId,
    })
  }

  const showSelectedTable = () => {
    switch (searchResultCategory) {
      case 'component':
        return (
          <ComponentResults
            results={resultsByComponent}
            isLoading={isLoading}
            setSelectedRowKeys={setSelectedRowKeys}
            selectedRowKeys={selectedRowKeys}
            activeFilters={componentActiveFilters}
            setActiveFilters={setComponentActiveFilters}
          />
        )
      case 'device':
        return (
          <DeviceResults
            results={resultsByDevice}
            isLoading={isLoading}
            setSelectedRowKeys={setSelectedRowKeys}
            selectedRowKeys={selectedRowKeys}
          />
        )
      case 'circuit':
        return (
          <CircuitResults
            results={resultsByCircuits}
            isLoading={isLoading}
            setSelectedRowKeys={setSelectedRowKeys}
            selectedRowKeys={selectedRowKeys}
            activeFilters={circuitActiveFilters}
            setActiveFilters={setCircuitActiveFilters}
            makeModel={makeModel}
          />
        )
      case 'dtc':
        return <DtcResults results={resultsByDtc} isLoading={isLoading} />
      case 'functional-diagrams':
        return (
          <FunctionalDiagramsResults
            results={resultsByFunctionalDiagrams}
            isLoading={isLoading}
          />
        )
      default:
        return ''
    }
  }

  const onSVGLoad = (idArray) => {
    const idString = idArray
      .map((element) => element.substring(1))
      .toString(',')
    updateComponentListSearchString(idString)
  }

  const removeSelectionHandler = () => {
    updateComponentListState(null)
    updateSelectedElementId(null)
  }

  const showLeftPanelContent = (intl, componentId) => {
    switch (searchResultCategory) {
      case 'component':
      case 'device':
      case 'circuit':
        return (
          <>
            <Row className="panel-header">
              <Col offset={1}>
                <FormattedMessage id="globalSearch.component" />: {componentId}
              </Col>
            </Row>
            <Divider size="small" />
            <Flex vertical gap="small" className="left-panel-body">
              <LocationOrPinout
                intl={intl}
                clickHandler={onSVGElementClick}
                onSVGLoad={onSVGLoad}
                selectedElementId={selectedElementId}
                loactionOrPinout={loactionOrPinout}
                setLocationOrPinout={setLocationOrPinout}
                makeModel={makeModel}
                searchResultCategory={searchResultCategory}
              />
              {searchSubsystems && (
                <div>
                  <RelatedSubsystemTable />
                </div>
              )}
              {componentListState && loactionOrPinout == LOCATION && (
                <SVGSelectionTable
                  componentList={componentListState}
                  onRowSelect={onSVGTableRowSelect}
                />
              )}
            </Flex>
          </>
        )
      case 'dtc':
        return (
          <>
            <Row className="panel-header">
              <Col offset={1}>
                <FormattedMessage id="globalSearch.manualDtc.title" />
              </Col>
            </Row>
            <Divider size="small" />
            <div className="left-panel-body-dtc">
              <ManualDtcs />
            </div>
          </>
        )
      default:
        return ''
    }
  }

  const RelatedSubsystemTable = () => {
    const globalSearchContext = useGlobalSearchContext()

    const selectSubsystem = ({ record }) => {
      history.push({
        pathname: `/functional-diagrams/vin/${globalSearchContext.vin}`,
        search: `?subsystem=${record.id}`,
      })
    }

    return (
      <SubsystemsViewTable
        subsystems={searchSubsystems}
        pagination={{ pageSize: 2 }}
        susbsystemCallback={selectSubsystem}
      />
    )
  }

  const onTextChangeCallback = (event) => {
    updateSearchStr(event.target.value)
  }

  const onTextSearchCallback = (event) => {
    resetState()
    updateComponentListState(null)
    if (!event || event.length == 0) {
      updateSearchStr(null)
      globalSearchDispatch.handleSearch({
        vin,
        searchString: null,
      })
    } else {
      dispatchEvent &&
        userPlant &&
        userName &&
        userId &&
        dispatchEvent('GLOBAL_SEARCH_PERFORMED', {
          vin,
          plant: userPlant,
          username: userName,
          user_id: userId,
          searchString: searchStr,
          session_id: sessionId.current,
        })
      globalSearchDispatch.handleSearch({
        vin,
        searchString: searchStr,
      })
    }
  }

  const resetState = () => {
    updateComponentListSearchString(null)
    updateSelectedElementId(null)
    setSelectedRowKeys([])
    setCircuitActiveFilters([])
    setComponentActiveFilters([])
  }

  return (
    <Flex vertical={true} id="global-search-page">
      <Splitter className="global-search-container">
        <Splitter.Panel defaultSize="50%" min="20%" max="70%">
          <div className="global-search-left-panel">
            {showLeftPanelContent(
              intl,
              globalSearchContext.selection.componentId,
            )}
          </div>
        </Splitter.Panel>
        <Splitter.Panel>
          <div className="global-search-right-panel">
            <Row className="panel-header">
              <Col offset={1}>
                <FormattedMessage id="globalSearch.searchInVehicle" />
              </Col>
            </Row>
            <Divider size="small" />
            <Flex vertical={true} gap="large" className="right-panel-body">
              <Flex vertical={false} gap="middle">
                <Input
                  data-testid="search-string"
                  placeholder={intl.formatMessage({
                    id: 'globalSearch.inputSearchTextPlaceholder',
                  })}
                  allowClear
                  loading={isLoading}
                  onPressEnter={onTextSearchCallback}
                  onChange={onTextChangeCallback}
                  value={searchStr}
                />
                <Button
                  id="global-search-button"
                  onClick={onTextSearchCallback}
                  type="primary"
                >
                  <FormattedMessage id="globalSearch.searchButton" />
                </Button>
              </Flex>
              <Radio.Group
                value={searchResultCategory}
                buttonStyle="solid"
                size="middle"
                onChange={(event) =>
                  setSearchResultCategory(event.target.value)
                }
              >
                <Radio.Button value={SEARCH_RESULT_CATEGORY.component}>
                  <Flex
                    data-testid="results-switch-components"
                    vertical={false}
                    gap="middle"
                    className="switch-radio-option"
                  >
                    <FormattedMessage id="globalSearch.resultsSwitch.components" />
                    <Counter
                      isLoading={isLoading}
                      results={resultsByComponent}
                    />
                  </Flex>
                </Radio.Button>
                <Radio.Button value={SEARCH_RESULT_CATEGORY.device}>
                  <Flex
                    data-testid="results-switch-devices"
                    vertical={false}
                    gap="middle"
                    className="switch-radio-option"
                  >
                    <FormattedMessage id="globalSearch.resultsSwitch.devices" />
                    <Counter isLoading={isLoading} results={resultsByDevice} />
                  </Flex>
                </Radio.Button>
                <Radio.Button value={SEARCH_RESULT_CATEGORY.circuit}>
                  <Flex
                    vertical={false}
                    gap="middle"
                    data-testid="results-switch-circuits"
                    className="switch-radio-option"
                  >
                    <FormattedMessage id="globalSearch.resultsSwitch.circuits" />
                    <Counter
                      isLoading={isLoading}
                      results={resultsByCircuits}
                    />
                  </Flex>
                </Radio.Button>
                <Radio.Button value={SEARCH_RESULT_CATEGORY.dtc}>
                  <Flex
                    data-testid="results-switch-dtcs"
                    vertical={false}
                    gap="middle"
                    className="switch-radio-option"
                  >
                    <FormattedMessage id="globalSearch.resultsSwitch.dtc" />
                    <Counter isLoading={isLoading} results={resultsByDtc} />
                  </Flex>
                </Radio.Button>
                <Radio.Button
                  value={SEARCH_RESULT_CATEGORY['functional-diagrams']}
                >
                  <Flex
                    data-testid="results-switch-functional-diagrams"
                    vertical={false}
                    gap="middle"
                    className="switch-radio-option"
                  >
                    <FormattedMessage id="globalSearch.resultsSwitch.functional-diagrams" />
                    <Counter
                      isLoading={isLoading}
                      results={resultsByFunctionalDiagrams}
                    />
                  </Flex>
                </Radio.Button>
              </Radio.Group>
              {showSelectedTable()}
            </Flex>
          </div>
        </Splitter.Panel>
      </Splitter>
      <div className="global-search-bottom-panel">
        {searchResultCategory === 'dtc' && (
          <>
            <GenerateEphemeralVehicleTestButton />
            <Button
              onClick={() => {
                globalSearchDispatch.handleDtcsSelectionReset()
              }}
            >
              <FormattedMessage id="globalSearch.clearButton" />
            </Button>
          </>
        )}
      </div>
    </Flex>
  )
}

export default requireAuthentication(injectIntl(GlobalSearchPage))
